import { AVATAR_IDS, MAX_NAME_LENGTH, sanitizeName } from "@zg/shared";
import { onLocaleChange, t } from "../i18n/index.js";
import { renderAvatarPreview } from "../game/textures.js";
import { el } from "./dom.js";
import { diyaLamp, vigilBackdrop } from "./vigil.js";

export interface JoinResult {
  name: string;
  avatarId: string;
}

/**
 * The join screen: name, avatar, go. Plain DOM rather than Phaser UI so that
 * phones get their native keyboard and autocorrect behaviour — and so the
 * first paint does not wait for the game engine to load.
 *
 * ## The gate, not a form
 *
 * It is a form, and it was drawn as one: a slab of panel on a flat field, the
 * heading ranged left like a settings page. But it is also the threshold of a
 * memorial, and the first thing most people will ever see of it.
 *
 * So the card now stands in the same lamplight the loading curtain did, with
 * the diya over the heading and the heading centred under it. The fields stay
 * ranged left — that is where you read a form from, and centring those would
 * be the mistake in the other direction.
 */
export class JoinPanel {
  private readonly root: HTMLDivElement;
  private readonly kicker: HTMLParagraphElement;
  private readonly heading: HTMLHeadingElement;
  private readonly subtitle: HTMLParagraphElement;
  private readonly nameFieldLabel: HTMLLabelElement;
  private readonly avatarFieldLabel: HTMLLabelElement;
  private readonly nameInput: HTMLInputElement;
  private readonly errorLine: HTMLParagraphElement;
  private readonly submitButton: HTMLButtonElement;
  private readonly unsubscribe: () => void;

  private avatarId: string = AVATAR_IDS[0];
  /** Errors are held as keys so they survive a language switch. */
  private errorKey = "";
  private busy = false;

  constructor(
    private readonly parent: HTMLElement,
    private readonly onJoin: (result: JoinResult) => Promise<void>,
  ) {
    this.nameInput = el("input", {
      className: "zg-input",
      attrs: {
        type: "text",
        maxlength: String(MAX_NAME_LENGTH),
        autocomplete: "off",
        autocapitalize: "words",
        spellcheck: "false",
      },
    });
    this.nameInput.id = "zg-name";
    // Clear the complaint the moment they start fixing it — leaving a stale
    // rejection under a name they have already corrected reads as broken.
    this.nameInput.addEventListener("input", () => {
      if (this.errorKey) this.showErrorKey("");
    });

    this.errorLine = el("p", { className: "zg-error", attrs: { role: "alert" } });
    this.submitButton = el("button", { className: "zg-button", attrs: { type: "submit" } });
    this.nameFieldLabel = el("label", { className: "zg-label", attrs: { for: "zg-name" } });
    this.avatarFieldLabel = el("label", { className: "zg-label" });

    const form = el("form", {
      className: "zg-form",
      children: [
        this.nameFieldLabel,
        this.nameInput,
        this.avatarFieldLabel,
        this.buildAvatarPicker(),
        this.errorLine,
        this.submitButton,
      ],
    });
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      void this.handleSubmit();
    });

    this.kicker = el("p", { className: "zg-kicker" });
    this.heading = el("h1", { className: "zg-title" });
    this.subtitle = el("p", { className: "zg-sub" });

    const header = el("div", {
      className: "zg-card-head",
      children: [diyaLamp(), this.kicker, this.heading, this.subtitle],
    });

    this.root = el("div", {
      className: "zg-overlay is-vigil",
      children: [
        vigilBackdrop(),
        el("div", {
          className: "zg-card",
          children: [header, form],
        }),
      ],
    });

    this.parent.appendChild(this.root);
    this.applyStrings();
    this.unsubscribe = onLocaleChange(() => this.applyStrings());
  }

  /** Repaint every label in the current language. */
  private applyStrings(): void {
    this.kicker.textContent = t("join.date");
    this.heading.textContent = t("join.title");
    this.subtitle.textContent = t("join.subtitle");
    this.nameFieldLabel.textContent = t("join.nameLabel");
    this.nameInput.placeholder = t("join.namePlaceholder");
    this.nameInput.setAttribute("aria-label", t("join.nameLabel"));
    this.avatarFieldLabel.textContent = t("join.avatarLabel");
    this.submitButton.textContent = t(this.busy ? "join.submitBusy" : "join.submit");
    this.root.querySelectorAll<HTMLElement>(".zg-avatar").forEach((node, index) => {
      node.setAttribute("aria-label", `${t("join.avatarAria")} ${index + 1}`);
    });
    if (this.errorKey) this.errorLine.textContent = t(this.errorKey);
  }

  focus(): void {
    this.nameInput.focus();
  }

  private buildAvatarPicker(): HTMLElement {
    const row = el("div", { className: "zg-avatars", attrs: { role: "radiogroup" } });

    AVATAR_IDS.forEach((id, index) => {
      const button = el("button", {
        className: `zg-avatar${index === 0 ? " is-selected" : ""}`,
        attrs: {
          type: "button",
          role: "radio",
          "aria-checked": index === 0 ? "true" : "false",
          "data-avatar": id,
        },
        children: [renderAvatarPreview(index, 2)],
      });

      button.addEventListener("click", () => {
        this.avatarId = id;
        row.querySelectorAll(".zg-avatar").forEach((node) => {
          const selected = node === button;
          node.classList.toggle("is-selected", selected);
          node.setAttribute("aria-checked", selected ? "true" : "false");
        });
      });

      row.appendChild(button);
    });

    return row;
  }

  private async handleSubmit(): Promise<void> {
    const check = sanitizeName(this.nameInput.value);
    if (!check.ok) {
      this.showErrorKey(`name.${check.reason ?? "empty"}`);
      this.nameInput.focus();
      return;
    }

    this.showErrorKey("");
    this.setBusy(true);
    try {
      await this.onJoin({ name: check.value, avatarId: this.avatarId });
      this.hide();
    } catch (err) {
      console.error("[join] failed", err);
      this.showErrorKey("join.failed");
      this.setBusy(false);
    }
  }

  private setBusy(busy: boolean): void {
    this.busy = busy;
    this.submitButton.disabled = busy;
    this.submitButton.textContent = t(busy ? "join.submitBusy" : "join.submit");
    this.nameInput.disabled = busy;
  }

  showErrorKey(key: string): void {
    this.errorKey = key;
    this.errorLine.textContent = key ? t(key) : "";
    this.errorLine.classList.toggle("is-visible", key.length > 0);
  }

  hide(): void {
    this.root.classList.add("is-hidden");
  }

  destroy(): void {
    this.unsubscribe();
    this.root.remove();
  }
}
